import { Box, Container, Grid, Typography, alpha } from "@mui/material";
import { useInView } from "framer-motion";
import { motion } from 'framer-motion';
import { useRef } from "react";
import P from "../theme/palette";
import PaletteIcon from '@mui/icons-material/Palette';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { useTenantConfig } from "../hooks/useTenant";

const S = { main: '#b91010ff', light: '#d33434ff', dark: '#960505ff' };

const slideLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const slideRight = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const CUSTOMIZATION = [
  { icon: PaletteIcon, title: 'Arka plan rengi', gradient: `linear-gradient(135deg, ${S.main} 0%, ${S.dark} 100%)` },
  { icon: FormatSizeIcon, title: 'Başlık ve metin fontları', gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' },
  { icon: ColorLensIcon, title: 'Renkler ve boyutlar', gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' },
  { icon: AspectRatioIcon, title: 'Görsel düzenleri', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
];

const CustomizationSection = ({ isMobile }) => {
  const {tenantName} = useTenantConfig();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 8, md: 14 },
        background: '#f8fafc',
        borderTop: '1px solid #f1f5f9',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">

          {/* SOL - Büyük Widget Preview */}
          <Grid item xs={12} md={5}>
            <motion.div variants={slideLeft} initial="hidden" animate={isInView ? 'visible' : 'hidden'}>
              <Box sx={{
                p: 4, borderRadius: 4,
                background: '#ffffff',
                border: `1.5px solid ${alpha(S.main, 0.2)}`,
                boxShadow: `0 8px 32px ${alpha(S.main, 0.08)}`,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(S.main, 0.02)} 0%, ${alpha(S.light, 0.03)} 100%)`,
                  pointerEvents: 'none',
                },
              }}>
                {/* Simüle widget preview */}
                <Box sx={{
                  p: 3, borderRadius: 2, mb: 3,
                  background: `linear-gradient(135deg, ${alpha(S.main, 0.05)} 0%, ${alpha(S.light, 0.03)} 100%)`,
                  border: `1px solid ${alpha(S.main, 0.15)}`,
                  position: 'relative',
                  zIndex: 1,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <DashboardCustomizeIcon sx={{ fontSize: 18, color: S.main }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                      Widget Önizleme
                    </Typography>
                  </Box>
                  {/* Renk swatches */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {[S.main, S.light, '#6366f1', '#10b981', '#ec4899'].map((c, i) => (
                      <Box key={i} sx={{
                        width: 28, height: 28, borderRadius: '50%', bgcolor: c,
                        border: i === 0 ? '2.5px solid #fff' : 'none',
                        boxShadow: i === 0 ? `0 0 0 2px ${c}` : 'none',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.2)' },
                      }} />
                    ))}
                  </Box>
                  {/* Sahte font seçenekleri */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['Aa', 'Bb', 'Cc'].map((f, i) => (
                      <Box key={i} sx={{
                        px: 2, py: 0.8, borderRadius: 1.5,
                        border: `1.5px solid ${i === 0 ? S.main : P.neutral[200]}`,
                        background: i === 0 ? alpha(S.main, 0.06) : '#fff',
                        fontSize: '0.8rem', fontWeight: 700,
                        color: i === 0 ? S.main : P.neutral[400],
                        transition: 'all 0.2s',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: i !== 0 ? 'translateY(-2px)' : 'none',
                          borderColor: i !== 0 ? S.light : S.main,
                        },
                      }}>
                        {f}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* 4 özelleştirme ikonu */}
                <Grid container spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
                  {CUSTOMIZATION.map((c, i) => {
                    const CIcon = c.icon;
                    return (
                      <Grid item xs={6} key={i}>
                        <Box sx={{
                          p: 3,
                          borderRadius: 2.5,
                          height: '100%',
                          background: '#ffffff',
                          border: '1px solid #e5e7eb',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1.5,
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(135deg, ${alpha(S.main, 0.03)} 0%, ${alpha(S.light, 0.05)} 100%)`,
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                            zIndex: 0,
                          },
                          '&:hover': {
                            background: '#fff',
                            borderColor: S.main,
                            boxShadow: `0 12px 40px ${alpha(S.main, 0.15)}, 0 4px 12px ${alpha(S.main, 0.08)}`,
                            transform: 'translateY(-8px) scale(1.02)',
                            '&::before': {
                              opacity: 1,
                            },
                            '& .custom-icon-box': {
                              transform: 'scale(1.15) rotate(5deg)',
                              background: `linear-gradient(135deg, ${alpha(S.main, 0.15)} 0%, ${alpha(S.light, 0.12)} 100%)`,
                              borderColor: S.main,
                            },
                            '& .custom-icon': {
                              transform: 'scale(1.1)',
                            },
                          },
                        }}>
                          {/* İkon Kutusu */}
                          <Box className="custom-icon-box" sx={{
                            width: 56,
                            height: 56,
                            flexShrink: 0,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: c.gradient,
                            border: `1.5px solid ${alpha(S.main, 0.2)}`,
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            zIndex: 1,
                          }}>
                            <CIcon className="custom-icon" sx={{ 
                              fontSize: 28, 
                              color: 'white',
                              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                            }} />
                          </Box>
                          {/* Başlık */}
                          <Typography sx={{ 
                            fontSize: '0.85rem', 
                            fontWeight: 700, 
                            color: '#0f172a',
                            textAlign: 'center',
                            lineHeight: 1.3,
                            position: 'relative',
                            zIndex: 1,
                          }}>
                            {c.title}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </motion.div>
          </Grid>

          {/* SAĞ - Metin */}
          <Grid item xs={12} md={6}>
            <motion.div
              variants={slideRight}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  color: '#0f172a',
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  mb: 2.5,
                }}
              >
              Tasarım Kontrolü{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, ${S.main} 0%, ${S.dark} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Sizde
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '1rem',
                  lineHeight: 1.75,
                  mb: 4,
                }}
              >
                {tenantName}’in esnek tasarım altyapısı sayesinde web sitenizin renklerini, tipografisini ve içerik düzenini tamamen derneğinizin kimliğine göre şekillendirebilirsiniz.
              </Typography>

              {/* Özellik listesi */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  'Renk ve tipografi kontrolü',
                  'Widget bazlı sayfa düzeni',
                  'Her sayfa için özgün tasarım',
                  'Sürükle bırak içerik bloklar'

                ].map((item, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(S.main, 0.1)}`,
                      background: `linear-gradient(135deg, ${alpha(S.main, 0.02)} 0%, #fafafa 100%)`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '3px',
                        background: `linear-gradient(180deg, ${S.main} 0%, ${S.light} 100%)`,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${S.main} 0%, ${S.light} 100%)`,
                        flexShrink: 0,
                        boxShadow: `0 2px 8px ${alpha(S.main, 0.3)}`,
                      }}
                    />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#0f172a' }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e5e7eb' }}>
                <Typography sx={{
                  fontSize: '1rem',
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontStyle: 'italic',
                }}>
                 Her dernek aynı sistemi kullanır, ama her site farklı görünür.
                </Typography>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CustomizationSection;